import { Link } from 'react-router-dom';
import { useQC } from './hooks/useQC';
import { useState } from 'react';

export default function QCOrdersList() {
  const { orders } = useQC();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  
  if (orders.isLoading) return <div className="p-6">Loading...</div>;
  
  const filteredOrders = orders.data?.filter((order: any) => {
    const matchesFilter = filter === 'all' || order.status === filter;
    const matchesSearch = search === '' || 
      order.orderNo.toLowerCase().includes(search.toLowerCase()) ||
      order.product.toLowerCase().includes(search.toLowerCase()) ||
      order.customer.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'approved';
      case 'rejected': return 'suspended';
      case 'in-progress': return 'pending';
      default: return 'pending';
    }
  };
  
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };
  
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">QC Orders</h1>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Search orders..."
              className="input pl-10 w-64"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input w-40"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in-progress">In Progress</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="card bg-blue-50 border-blue-200">
          <div className="text-sm text-blue-600">Pending</div>
          <div className="text-xl font-bold text-blue-900">
            {orders.data?.filter((o: any) => o.status === 'pending').length || 0}
          </div>
        </div>
        <div className="card bg-yellow-50 border-yellow-200">
          <div className="text-sm text-yellow-600">In Progress</div>
          <div className="text-xl font-bold text-yellow-900">
            {orders.data?.filter((o: any) => o.status === 'in-progress').length || 0}
          </div>
        </div>
        <div className="card bg-green-50 border-green-200">
          <div className="text-sm text-green-600">Approved</div>
          <div className="text-xl font-bold text-green-900">
            {orders.data?.filter((o: any) => o.status === 'approved').length || 0}
          </div>
        </div>
        <div className="card bg-red-50 border-red-200">
          <div className="text-sm text-red-600">Rejected</div>
          <div className="text-xl font-bold text-red-900">
            {orders.data?.filter((o: any) => o.status === 'rejected').length || 0}
          </div>
        </div>
      </div>
      
      {/* Orders Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full text-sm">
          <thead className="bg-[var(--color-accent)]/40">
            <tr>
              <th className="th">Order #</th>
              <th className="th">Product</th>
              <th className="th">Customer</th>
              <th className="th">Vendor</th>
              <th className="th">Priority</th>
              <th className="th">Due Date</th>
              <th className="th">Status</th>
              <th className="th">Assigned To</th>
              <th className="th">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders?.map((order: any) => (
              <tr key={order.id} className="tr">
                <td className="td">
                  <div className="font-medium">{order.orderNo}</div>
                  <div className="text-xs text-gray-500">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </div>
                </td>
                <td className="td">
                  <div className="font-medium">{order.product}</div>
                </td>
                <td className="td">{order.customer}</td>
                <td className="td">{order.vendor}</td>
                <td className="td">
                  <span className={`badge ${getPriorityColor(order.priority)}`}>
                    {order.priority}
                  </span>
                </td>
                <td className="td">
                  <div className="text-sm">
                    {new Date(order.dueDate).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(order.dueDate).toLocaleTimeString()}
                  </div>
                </td>
                <td className="td">
                  <span className={`badge ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </td>
                <td className="td">
                  {order.assignedTo || (
                    <span className="text-gray-400 italic">Unassigned</span>
                  )}
                </td>
                <td className="td">
                  <div className="flex items-center space-x-2">
                    <Link 
                      to={`/qc/orders/${order.id}`} 
                      className="link flex items-center"
                    >
                      <i className="ri-search-line mr-1"></i>
                      Inspect
                    </Link>
                    {order.status === 'pending' && (
                      <button className="text-blue-600 hover:text-blue-800 text-xs">
                        <i className="ri-play-line mr-1"></i>
                        Start
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredOrders?.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <i className="ri-inbox-line text-4xl mb-2"></i>
            <div>No orders found matching your criteria</div>
          </div>
        )}
      </div>
    </div>
  );
}